import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, CircularProgress, IconButton, ButtonGroup } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface PDFRendererProps {
  url?: string;
  file?: File;
  filename?: string;
  mode?: 'preview' | 'thumbnail' | 'dialog';
  width?: string | number;
  height?: string | number;
  onLoad?: () => void;
  onError?: (error?: any) => void;
  onClick?: () => void;
}

export const PDFRenderer: React.FC<PDFRendererProps> = React.memo(({
  url,
  file,
  filename,
  mode = 'preview',
  width = '100%',
  height = '100%',
  onLoad,
  onError,
  onClick
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] = useState<number>(0);
  const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);
  const [scale, setScale] = useState<number>(1.0); // PDF.js native scale

  // Resolve the PDF source (file or URL) - prioritize URL to prevent memory leaks
  const pdfSource = useMemo(() => {
    // If URL is provided, use it directly - don't use file blob
    if (url) {
      return url;
    }
    // Only use file directly if no URL was provided
    if (file) {
      return file; // PDF.js Document component accepts File objects directly
    }
    return undefined;
  }, [url, file]);

  console.log('[PDFRenderer] Rendering with source:', { url, file: file?.name, resolved: pdfSource });

  // Memoize options to prevent unnecessary reloads - aggressive memory optimization
  const options = useMemo(() => {
    const baseOptions = {
      cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
      withCredentials: true,
      disableAutoFetch: true, // Only load pages when needed
      disableRange: false, // Enable range requests for streaming
      disableStream: false, // Enable streaming
      maxImageSize: 1024 * 1024 * 1, // Reduced to 1MB max image size for memory
      isEvalSupported: false, // Disable eval for security
      verbosity: 0, // Reduce logging
    };

    // Optimize based on mode and source type
    if (mode === 'preview' || mode === 'thumbnail') {
      // Preview mode: Aggressive memory optimization
      return {
        ...baseOptions,
        rangeChunkSize: 32 * 1024, // Smaller 32KB chunks for previews
        stopAtErrors: true, // Stop at first error to avoid loading entire doc
        disableAutoFetch: true, // Critical: only load what we need
        pdfBug: false, // Disable debugging features
      };
    } else {
      // Dialog mode: Better user experience but still optimized
      return {
        ...baseOptions,
        rangeChunkSize: 128 * 1024, // Larger 128KB chunks for full viewing
      };
    }
  }, [mode]);

  // Memory optimization: In preview mode, only show page 1
  const maxPagesToRender = mode === 'preview' || mode === 'thumbnail' ? 1 : numPages;
  const effectivePageNumber = mode === 'preview' || mode === 'thumbnail' ? 1 : pageNumber;

  // Optimize scale for memory usage
  const getOptimalScale = useCallback(() => {
    if (mode === 'thumbnail') return 0.3; // Very small for thumbnails
    if (mode === 'preview') return 0.6;   // Small for previews  
    return scale; // Full scale for dialog mode
  }, [mode, scale]);

  const effectiveScale = getOptimalScale();

  // Memoize callback functions to prevent re-renders
  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    setLoadProgress(100);
    if (onLoad) onLoad();
  }, [onLoad]);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('[PDFRenderer] PDF load error:', error);
    setError('Failed to load PDF');
    setLoading(false);
    setLoadProgress(0);
    if (onError) onError(error);
  }, [onError]);

  const onLoadProgress = useCallback((progress: { loaded: number; total: number }) => {
    if (progress.total > 0) {
      const percentage = Math.round((progress.loaded / progress.total) * 100);
      setLoadProgress(percentage);
    }
  }, []);

  const goToPrevPage = useCallback(() => {
    // In preview mode, navigation is disabled for memory optimization
    if (mode === 'preview' || mode === 'thumbnail') return;
    setPageNumber(prev => Math.max(1, prev - 1));
  }, [mode]);

  const goToNextPage = useCallback(() => {
    // In preview mode, navigation is disabled for memory optimization
    if (mode === 'preview' || mode === 'thumbnail') return;
    setPageNumber(prev => Math.min(numPages ?? 1, prev + 1));
  }, [numPages, mode]);

  // Zoom controls - native PDF.js scaling
  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(prev * 1.25, 3.0)); // Max 3x zoom
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => Math.max(prev / 1.25, 0.25)); // Min 0.25x zoom
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1.0);
  }, []);

  // Reset state when PDF source changes
  useEffect(() => {
    setNumPages(null);
    setPageNumber(1);
    setLoading(true);
    setError(null);
    setLoadProgress(0);
    setScale(1.0); // Reset zoom when source changes
  }, [pdfSource]);

  // Update viewport height on window resize
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Early return if no PDF source provided
  if (!pdfSource) {
    return (
      <Box sx={{ 
        width, 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.1)',
        borderRadius: 1
      }}>
        <Typography color="text.secondary">No PDF source provided</Typography>
      </Box>
    );
  }

  // Thumbnail mode
  if (mode === 'thumbnail') {
    return (
      <Box 
        sx={{ 
          width, 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 1
        }}
        onClick={onClick}
      >
        <Typography>📄 PDF</Typography>
      </Box>
    );
  }

  // Memory-optimized navigation controls
  const shouldShowNavigation = mode === 'dialog' && (numPages ?? 0) > 1;
  const isNavigationDisabled = mode !== 'dialog';

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      width: '100%',
      height: '100%',
      maxHeight: '80vh', // Maximum height 80% of viewport height
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      borderRadius: 2,
      overflow: 'hidden'
    }}>
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%',
          gap: 2
        }}>
          <CircularProgress 
            variant="determinate" 
            value={loadProgress} 
            size={48}
            sx={{
              color: theme => theme.palette.primary.main,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Loading PDF... {loadProgress}%
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            opacity: 0.7
          }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              backgroundColor: 'primary.main',
              animation: 'pulse 1.5s ease-in-out infinite'
            }} />
            <Typography variant="caption" color="text.secondary">
              Streaming content
            </Typography>
          </Box>
        </Box>
      )}
      
      {error && (
        <Box sx={{ margin: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      
      {/* PDF Content Area - with native zoom and scrolling */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '100%',
        overflow: 'auto', // Enable scrolling when content overflows
        minHeight: 0,
        position: 'relative',
        '& .react-pdf__Document': {
          display: 'flex',
          justifyContent: 'center',
          width: 'fit-content'
        },
        '& .react-pdf__Page': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '0 auto',
          '& canvas': {
            display: 'block',
            border: '1px solid rgba(0,0,0,0.1)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }
        }
      }}>
        <Document
          file={pdfSource}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          onLoadProgress={onLoadProgress}
          options={options}
          loading=""
        >
          <Page
            pageNumber={effectivePageNumber}
            scale={effectiveScale} // Use native PDF.js scaling
            renderTextLayer={false} // Disable text layer for better performance
            renderAnnotationLayer={false} // Disable annotations for better performance
            loading=""
            onLoadSuccess={() => {
              // Only load additional pages when user navigates to them
              console.log(`[PDFRenderer] Page ${effectivePageNumber} loaded successfully`);
            }}
            onLoadError={(error) => {
              console.error(`[PDFRenderer] Page ${effectivePageNumber} load error:`, error);
            }}
          />
        </Document>
      </Box>
      
      {/* Zoom Controls - always visible when PDF is loaded */}
      {!loading && !error && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100%',
          padding: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          flexShrink: 0
        }}>
          <ButtonGroup variant="contained" size="small" sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
            <IconButton 
              onClick={zoomOut} 
              disabled={scale <= 0.25}
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' } }}
              title="Zoom Out"
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            
            <IconButton 
              onClick={resetZoom}
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' } }}
              title="Reset Zoom"
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
            
            <IconButton 
              onClick={zoomIn} 
              disabled={scale >= 3.0}
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' } }}
              title="Zoom In"
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </ButtonGroup>
          
          <Typography variant="caption" sx={{ color: 'white', ml: 2 }}>
            {Math.round(scale * 100)}%
          </Typography>
        </Box>
      )}
      
      {/* Page Navigation Controls - only visible for multi-page PDFs */}
      {shouldShowNavigation && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          padding: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          flexShrink: 0
        }}>
          <IconButton 
            onClick={goToPrevPage} 
            disabled={isNavigationDisabled || pageNumber <= 1}
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' } }}
            title="Previous Page"
          >
            ⬅️
          </IconButton>
          
          <Typography variant="body2" sx={{ color: 'white' }}>
            {mode !== 'dialog'
              ? `Preview (Page 1 of ${numPages}) - Memory Optimized`
              : `Page ${pageNumber} of ${numPages}`
            }
          </Typography>
          
          <IconButton 
            onClick={goToNextPage} 
            disabled={isNavigationDisabled || pageNumber >= (numPages ?? 1)}
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' } }}
            title="Next Page"
          >
            ➡️
          </IconButton>
        </Box>
      )}
    </Box>
  );
});

export default PDFRenderer; 